<template>
  <bean-source-page
    :type="type"
    :columns="columns"
    :form-columns="formColumns"
    :filter-columns="filterColumns"
    :actions="actions"
    :form="form"
    :title="$t('model.followRecord')"
    resource="follow_records"
    index-exec-filter-router-type="push"
    :onFetchData="onFetchData"
    ref="sourcePageRef"
  >
    <template slot="action">
      <bean-import-button
        can='follow_records.create'
        :buttonText="$t('import')"
        :tooltipText="$t('importTemplate')"
        url="/follow_records/import"
        :loadingToast="$t('importLoadingToast')"
        :fileChangeHandler="handleImprtFileChange"
      >
        <template slot="download">
          <el-popover trigger="hover">
            <div style="cursor: pointer;" @click="downloadTpl">下载模板</div>
            <i class="el-icon-info btn-tip-trigger" slot="reference" @click.stop></i>
          </el-popover>
        </template>
      </bean-import-button>
    </template>
  </bean-source-page>
</template>

<script>
import { Vue, Component, Prop, Mixins } from 'vue-property-decorator';
import { filterColumnsExtend, columnsExtend, formColumnsExtend } from '../admin_grapes/follow_records.js';
import { abilityService } from 'beans-admin-plugin';
import Utils from '../components/utils.vue';
import _ from 'lodash';
import { displayText } from '@/custom_models_extend';
import moment from 'moment';
import DialogText from '@/components/dialog-text.vue';

@Component
export default class FollowRecords extends Mixins(Vue, Utils) {
  @Prop(String) type;

  // form 新建表单初始化数据（default数据）
  form = {}

  defaultCustomerOptions = []

  /**
   * @param {Function} h - createElement
   * @param {Object} params
   * @param {String} params.type - page type enum: ['index', 'show', 'edit', 'new']
   * @param {String} params.resource -RESTful api resource name
   */
  filterColumns() {
    const array = [
      abilityService.can('users.read_options') && {
        key: 'user_id_eq',
        renderCell: {
          props: {
            clearable: true,
            xRemotePreload: async () => {
              return (await this.$request.get('users/read_options', { per_page: 100, ...this.userOptionsFilter })).data.map(item => ({ value: item.id, label: displayText(item) }));
            },
            xRemoteSearch: async (keyword) => {
              return (await this.$request.get('users/read_options', { keyword, ...this.userOptionsFilter })).data.map(item => ({ value: item.id, label: displayText(item) }));
            }
          },
        }
      },
      {
        key: 'customer_id_eq',
        renderCell: {
          props: {
            xRemotePreload: async () => {
              return (await this.$request.get('customers/read_options', { per_page: 100, ...this.customerOptionsFilter })).data.map(item => ({ value: item.id, label: item.name }));
            },
            xRemoteSearch: async (keyword) => {
              return (await this.$request.get('customers/read_options', { keyword, ...this.customerOptionsFilter })).data.map(item => ({ value: item.id, label: item.name }));
            }
          }
        }
      },
      {
        prop: 'followed_at_range',
        label: this.$t('followRecord.followedAt'),
        renderCell: {
          component: 'dateTimePicker',
          props: {
            type: 'datetimerange'
          }
        }
      },
    ].filter(Boolean)

    return filterColumnsExtend(array)
  }

  /**
   * @param {Function} h - createElement
   * @param {Object} params
   * @param {String} params.type - page type enum: ['index', 'show', 'edit', 'new']
   * @param {String} params.resource -RESTful api resource name
   */
  columns(h, { type }) {
    const array = [
      type === 'show' && 'id',
      'user',
      'customer',
      {
        prop: 'customer.customer_level.name',
        label: this.$t('customer.customerLevel'),
      },
      { key: 'followed_at', sortable: true },
      {
        key: 'follow_key_items',
        minWidth: 150,
        renderCell: (h, { props: { value } }) => {
          if (value && value.length) {
            return <div>{value.map(item => item.name).join('、')}</div>
          } else {
            return <span>-</span>
          }
        }
      },
      {
        key: 'content',
        minWidth: 200,
        renderCell: (h, { props: { value } }) => {
          return <DialogText
            title="跟进内容"
            value={value}
            maxRow={3}
            showAll={type === 'show'}
          />
        }
      },
      {
        key: 'media',
        minWidth: 150,
      },
      type === 'show' && {
        key: 'task_assignment',
        renderCell: (h, { props: { value } }) => {
          if (value) {
            return <bean-link keepNode to={{ name: 'tasks.show', params: { id: value.task_id } }}>{displayText(value)}</bean-link>
          } else {
            return <span>/</span>
          }
        },
      },
      type === 'show' && 'created_at',
      type === 'show' && 'updated_at',
    ].filter(Boolean)

    return columnsExtend(array)
  }

  /**
   * @param {Function} h - createElement
   * @param {Object} params
   * @param {String} params.type - page type enum: ['index', 'show', 'edit', 'new']
   * @param {String} params.resource - RESTful api resoure name
   * @param {Object} params.data - current form data
   * @param {Function} params.change - invoke this method to sync form change({ field: 'new value' })
   */
  formColumns(h, { data, type }) {
    const array = [
      {
        key: 'customer_id',
        renderCell: {
          props: {
            options: this.defaultCustomerOptions,
            xRemotePreload: async () => {
              return (await this.$request.get('customers/read_options', { per_page: 100, ...this.customerOptionsFilter })).data.map(item => ({ value: item.id, label: item.name }));
            },
            xRemoteSearch: async (keyword) => {
              return (await this.$request.get('customers/read_options', { keyword, ...this.customerOptionsFilter })).data.map(item => ({ value: item.id, label: item.name }));
            }
          },
          domProps: {
            style: 'width: 100%'
          }
        }
      },
      'followed_at',
      {
        key: 'follow_key_item_ids',
        renderCell: {
          props: {
            multiple: true,
            xRemotePreload: async () => {
              return (await this.$request.get('follow_key_items/read_options')).data.map(item => ({ value: item.id, label: item.name }));
            },
          },
        }
      },
      'content',
      {
        key: 'media',
        renderCell: {
          props: {
            accept: 'image/*,.csv,.pdf,.txt,.xlsx,.docx,.xls,.doc',
            type: ['image', 'csv', 'application/pdf', 'text/plain', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.ms-excel', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
            clipboard: true,
            drag: true,
          }
        }
      },
      'task_assignment_id',
    ].filter(Boolean)

    return formColumnsExtend(array, { data: data, type: type })
  }

  /**
   * @param {Object} scope -页面/表格 scope 数据
   * @param {Object} scope.row - 详情页/表格行 数据
   * @param {Object} sourcePageScope
   * @param {Function} sourcePageScope.remove - sourcePage删除方法
   * @param {Function} sourcePageScope.reload - sourcePage重新请求页面数据
   */
  actions({ row }, { remove }) {
    return [
      row.can_update && {
        component: 'routeButton',
        props: {
          label: this.$t('edit'),
          to: { name: 'follow_records.edit', params: { id: row.id } }
        }
      },
      row.can_destroy && {
        component: 'confirmButton',
        props: {
          label: this.$t('destroy'),
          type: 'danger',
        },
        on: {
          confirm: () => remove(row.id)
        }
      }
    ].filter(Boolean);
  }

  async downloadTpl() {
    const result = await this.$request.get('/follow_records/import_template', {}, { responseType: 'blob', return_res: true, timeout: 5 * 60 * 1000 })
    const a = document.createElement('a');
    document.body.appendChild(a);
    a.style = 'display: none';
    const blob = new Blob([result.data]);
    a.href = window.URL.createObjectURL(blob);
    a.download = '跟进记录导入模板.xlsx';
    a.click();
    document.body.removeChild(a);
  }

  async upload(file) {
    const form = new FormData();
    form.append('file_message', file);
    const requestConfig = {
      headers: { 'Content-Type': 'multipart/form-data' },
      responseType: 'blob',
      return_res: true,
      // 1小时超时
      timeout: 1 * 60 * 60 * 1000
    }
    const result = await this.$request.post('/follow_records/import', form, requestConfig);
    if (result.headers['x-error-message']) {
      throw new Error(decodeURIComponent(result.headers['x-error-message']));
    }
    if (+result.headers['x-error-count'] > 0) {
      this.download(new Blob([result.data]), `${this.$t('bean.error_massage')}.xlsx`);
      throw new Error(this.$t('bean.importFail'));
    }
  }

  // 为了支持导入成功之后刷新页面
  async handleImprtFileChange(e) {
    const file = e.target.files[0];
    this.importing = true;
    let toastInstance = null;
    if (this.loadingToast) {
      toastInstance = this.$message.info({
        message: this.loadingToast,
        duration: 0,
        showClose: true
      });
    }
    try {
      await this.upload(file);
      this.$message.success(this.$t('bean.importSuccess'));
      this.$refs.sourcePageRef.fetchData();
    } catch (e) {
      const msg = e.message || this.$t('bean.importFail');
      this.$message.error(msg);
    } finally {
      this.importing = false;
      if (toastInstance) {
        toastInstance.close();
      }
    }
    e.target.value = '';
  }

  async onFetchData({ type }) {
    if (type === 'index') {
      const { data, pagination } = await this.$request.get('/follow_records', { params: { ...this.$route.query } });
      return {
        data,
        pagination
      }
    }

    if (type === 'show' || type === 'edit') {
      const data = await this.$request.get(`/follow_records/${this.$route.params.id}`);
      return { data };
    }

    if (type === 'new') {
      if (_.get(this.$route, 'query.customer_id') && _.get(this.$route, 'query.customer_name')) {
        this.defaultCustomerOptions = [{ value: this.$route.query.customer_id, label: this.$route.query.customer_name }];

        return {
          data: {
            customer_id: this.$route.query.customer_id,
            followed_at: moment().format('YYYY-MM-DD HH:mm:ss')
          }
        }
      }
      return {
        data: {
          followed_at: moment().format('YYYY-MM-DD HH:mm:ss')
        }
      }
    }
  }
}

export const route = [
  {
    path: '/follow_records',
    name: 'follow_records.index',
    component: FollowRecords,
    props: {
      type: 'index',
    }
  },
  {
    path: '/follow_records/new',
    name: 'follow_records.new',
    component: FollowRecords,
    props: {
      type: 'new',
    }
  },
  {
    path: '/follow_records/:id',
    name: 'follow_records.show',
    component: FollowRecords,
    props: {
      type: 'show',
    }
  },
  {
    path: '/follow_records/:id/edit',
    name: 'follow_records.edit',
    component: FollowRecords,
    props: {
      type: 'edit'
    }
  }
]
</script>
